/** @module csi/gallery/_base */
define([
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/html"
], function(domClass, domStyle, query, csi, _, html) {

	var CSS_BLOCK = "galleryBlock";
	var CSS_NO_IMAGE = "csiGalleryNoImage";
	var BLOCK_ALIGN_KEY = "GalleryBlockHeight"
	var IMAGE_ALIGN_KEY = "GalleryImageHeight";

	html.registerDataAttribute("DATA_GALLERY_ALIGN_KEYS", "gallery-align", null, true);
	html.registerDataAttribute("DATA_GALLERY_GROUP_COLUMN", "gallery-gc", Number, true);
	html.registerDataAttribute("DATA_GALLERY_GROUP_ITEMS_PER_ROW", "gallery-items-per-row", Number, true);
	html.registerDataAttribute("DATA_GALLERY_GROUP_ROW", "gallery-gr", Number, true);
	html.registerDataAttribute("DATA_GALLERY_GROUP_STYLE", "gallery-group-style", null, true);
	html.registerDataAttribute("DATA_GALLERY_GROUP_WIDTH", "gallery-group-width", Number, true);
	html.registerDataAttribute("DATA_GALLERY_IMAGE_ON_TOP", "gallery-image-on-top", Boolean, true);
	html.registerDataAttribute("DATA_GALLERY_RESIZE_ATTRIBUTES", "gallery-resize-attrs", Boolean, true);
	html.registerDataAttribute("DATA_GALLERY_ROW", "gallery-row", Number, true);
	html.registerDataAttribute("DATA_GALLERY_ROWS_PER_PAGE", "gallery-page-rows", Number, true);

	/**
	 * Return max height in pixels of blocks in NodeList for given indices
	 * @param {NodeList} nl
	 * @param {number[]} blockIndices
	 * @return {string}
	 */
	function _getMaxBlockHeight(nl, blockIndices) {
		return _.reduce(blockIndices, function(h, nBlock) {
			return Math.max(h, domStyle.get(nl[nBlock], html.HEIGHT));
		}, 0) + html.PX;
	}

	/**
	 * Fix group headers to current height (for column headers) and current width (unless width is specified as custom view option)
	 * @param {DOMNode} thead
	 * @param {number} groupHeaderWidth
	 */
	function fixGroupHeaders(thead, groupHeaderWidth) {
		var firstCell = thead.firstChild.firstChild;
		var h = domStyle.get(firstCell, html.HEIGHT) + "px";
		var w = (groupHeaderWidth || domStyle.get(firstCell, html.WIDTH)) + "px";

		domStyle.set(firstCell, {
			height: h,
			maxWidth: w,
			width: w
		});

		query("th", thead.nextSibling).addClass("ellipsis").style({
			maxWidth: w,
			width: w
		});
	}

	/**
	 * Return all result blocks under given domNode
	 * @param {DOMNode} rootNode
	 * @return {NodeList}
	 */
	function getBlocks(rootNode) {
		return html.queryDom([".", CSS_BLOCK], rootNode);
	}

	/**
	 * Return row containing blocks (added from resizeBlocks function)
	 * @param {DOMNode} blockNode
	 * @return {DOMNode}
	 */
	function getBlockRow(blockNode) {
		return html.getDataAttribute(blockNode, html.DATA_GALLERY_ROW);
	}

	/**
	 * Return index of table column containing node
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getGroupColumn(domNode) {
		return html.getDataAttribute(domNode, html.DATA_GALLERY_GROUP_COLUMN, true);
	}

	/**
	 * Return grouped content nodes under given root that satisfy given selector (if any);
	 * selector specified to select only those nodes in a given column or row
	 * @param {DOMNode} rootNode
	 * @param {string} [cellSelector]
	 * @return {NodeList}
	 */
	function getGroupContentNodes(rootNode, cellSelector) {
		return query((cellSelector || "") + " .galleryViewPlotContent", rootNode);
	}

	/**
	 * Return index of table row containing node
	 * @param {DOMNode} domNode
	 * @return {number}
	 */
	function getGroupRow(domNode) {
		return html.getDataAttribute(domNode, html.DATA_GALLERY_GROUP_ROW, true);
	}

	/**
	 * Apply styling from one source block to another
	 * @param {HTMLElement} sourceNode 
	 * @param {HTMLElement} blockNode
	 * @param {string[]} keysToAlign
	 * @param {boolean} bResizeAttributesToImage - true when attributes below image and do not have a fixed width
	 */
	function copyBlockStyling(sourceNode, blockNode, keysToAlign, bResizeAttributesToImage) {
		const {DATA_TEMPLATE_ALIGN, HEIGHT, MAXWIDTH, WIDTH} = html;
		const styles = [];

		function add(element, dim, value) {
			styles.push({element, dim, value});
		}

		for (const key of keysToAlign) {
			if (key === BLOCK_ALIGN_KEY) {
				add(blockNode, HEIGHT, sourceNode.style[HEIGHT]);
			} else {
				const sourceList = query(html.getDataAttributeSelector(DATA_TEMPLATE_ALIGN, key), sourceNode);
				const blockList = query(html.getDataAttributeSelector(DATA_TEMPLATE_ALIGN, key), blockNode);
				const bImage = bResizeAttributesToImage && (key == IMAGE_ALIGN_KEY);

				blockList.forEach((node, i) => {
					const sourceNode = sourceList[i];

					add(node, HEIGHT, sourceNode.style[HEIGHT]);

					if (bImage) {
						add(node.nextSibling, WIDTH, sourceNode.nextSibling.style[WIDTH]);
						add(node.nextSibling, MAXWIDTH, sourceNode.nextSibling.style[MAXWIDTH]);
					}
				});
			}
		}

		for (const {dim, element, value} of styles) {
			element.style[dim] = value;
		}
	}

	/**
	 * Align designated nodes in template with those of other blocks in same row; this algorithm tries
	 * to optimize how many page renders need to be performed - we do not want it to depend on the number of blocks
	 * which is what the previous algorithm did. The number of rendering passes should be constant.
	 * @param {DOMNode} rootNode
	 * @param {string[]} keysToAlign
	 * @param {boolean} bPlot - true when gallery is grouped
	 * @param {boolean} bResizeAttributesToImage - true when attributes below image and do not have a fixed width
	 * @return {number} - returns number of rows of blocks
	 */
	function resizeBlocks(rootNode, keysToAlign, bPlot, bResizeAttributesToImage) {
		if (!rootNode) {
			return 0;
		}

		var nlBlocks = query("." + CSS_BLOCK, rootNode);
		var nBlocks = nlBlocks.length;
		if (!nBlocks) {
			return 0;
		}

		// 1. get node lists for nodes to align
		var bAlignBlocks;
		var nlImages;

		var nodeLists = csi.transform(keysToAlign, function(key) {
			if (key === BLOCK_ALIGN_KEY) {
				bAlignBlocks = true;
				return null;
			}

			var nl = query(html.getDataAttributeSelector(html.DATA_TEMPLATE_ALIGN, key), rootNode);

			if (key == IMAGE_ALIGN_KEY) {
				nlImages = nl;
			}

			return nl;
		});

		// 2. reset previous heights for all nodelists
		_.each(nodeLists, function(nl) {
			nl.style(html.HEIGHT, "");
		});

		if (bAlignBlocks) {
			nlBlocks.style(html.HEIGHT, "");
		}

		// 3. if template specifies, then sync attribute width with image width - this is so editable properties extend to full size of image
		if (nlImages && bResizeAttributesToImage) {
			nlImages.forEach(function(node, i) {
				domStyle.set(node.nextSibling, html.WIDTH, "");
				domStyle.set(node.nextSibling, html.MAXWIDTH, "");
			});

			var widths = nlImages.map(function(node) {
				// reference lastChild as the first child may be the image paste element
				var wi = (domClass.contains(node, CSS_NO_IMAGE) || node.firstChild.lastChild == null) ? 0 : domStyle.get(node.firstChild.lastChild, html.WIDTH);
				var wp = domStyle.get(node.nextSibling, html.WIDTH);
				return (wp < wi) ? wi : 0;
			});

			nlImages.forEach(function(node, i) {
				if (widths[i]) {
					html.setPixels(node.nextSibling, html.WIDTH, widths[i]);
					domStyle.set(node.nextSibling, html.MAXWIDTH, "inherit");
				}
			});
		}

		// 4. align blocks row by row
		var plotBlocks = bPlot && csi.hashArray(_.toArray(nlBlocks), Number);
		var blockIndex = 0;
		var blockIndices;
		var keyHeights;
		var rowHeight;
		var nRow = 0;

		var getBlocksInRow = bPlot ?
			function() {
				// summary: find blocks across all plot cells that lie in same visual row
				var top = -1;
				var a = [];

				_.each(plotBlocks, function(block, i) {
					var topBlock = block.offsetTop;

					if ((top < 0) || (top == topBlock)) {
						top = topBlock;
						a.push(i);
					}
				});

				_.each(a, function(i) {
					delete plotBlocks[i];
				});

				return a.length && a;
			} :
			function() {
				// summary: find blocks starting with start index that lie in same visual row
				var block = nlBlocks[blockIndex];
				if (!block) return null;

				var a = [blockIndex];
				var top = block.offsetTop;

				while (block = nlBlocks[++blockIndex]) {
					if (block.offsetTop != top) {
						break;
					}

					a.push(blockIndex);
				}

				return a;
			};

		while (blockIndices = getBlocksInRow()) {
			// track max height of those blocks in each row
			keyHeights = _.map(nodeLists, function(nl) {
				return _getMaxBlockHeight(nl, blockIndices);
			});

			_.each(blockIndices, function(nBlock) {
				// mark nodes with appropriate row index
				html.setDataAttribute(nlBlocks[nBlock], html.DATA_GALLERY_ROW, nRow);

				if (keyHeights) {
					// align heights for all keys for all blocks in same row
					_.each(nodeLists, function(nl, nKey) {
						domStyle.set(nl[nBlock], html.HEIGHT, keyHeights[nKey]);
					});
				}
			});

			// align overall block heights per row
			if (bAlignBlocks && keyHeights) {
				rowHeight = _getMaxBlockHeight(nlBlocks, blockIndices);

				_.each(blockIndices, function(nBlock) {
					domStyle.set(nlBlocks[nBlock], html.HEIGHT, rowHeight);
				});
			}

			++nRow;
		}

		return nRow;
	}

	return {
		CSS_BLOCK,
		CSS_NO_IMAGE,
		BLOCK_ALIGN_KEY,
		IMAGE_ALIGN_KEY,

		copyBlockStyling,
		fixGroupHeaders,
		getBlockRow,
		getBlocks,
		getGroupColumn,
		getGroupContentNodes,
		getGroupRow,
		resizeBlocks
	};
});